import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Settings, Building, MapPin, Users, HelpCircle, Plus, Pencil, Trash2, Save, FileText, Hash, UserPlus, Upload, Image, ShieldCheck, Loader2, DollarSign, Wrench, CalendarDays, Mail, Layers, ListChecks, PenLine, RotateCcw, PhoneCall, Timer, Send, Bell, CreditCard } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import SignatureCanvas from 'react-signature-canvas';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { JOB_CATEGORIES } from '@/constants/jobCategories';
import { ContactCombobox } from '@/components/common/ContactCombobox';
import { toast } from 'sonner';
import { invalidateCountiesCache } from '@/hooks/useCounties';
import JobStatusSettings from '@/components/settings/JobStatusSettings';
import EstimateStatusSettings from '@/components/settings/EstimateStatusSettings';
import CallRecordingSettings from '@/components/settings/CallRecordingSettings';
import MaintenanceTab from '@/components/settings/MaintenanceTab';

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (value) => {
  const digits = value.replace(/\D/g, '');
  const limited = digits.slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `(${limited}`;
  if (limited.length <= 6) return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
};

// Available roles for user creation
const USER_ROLES = [
  { value: 'admin', labelKey: 'settings.roles.admin' },
  { value: 'office', labelKey: 'settings.roles.office' },
  { value: 'driller', labelKey: 'settings.roles.driller' },
  { value: 'pump_installer', labelKey: 'settings.roles.pump_installer' },
  { value: 'operator', labelKey: 'settings.roles.operator' },
  { value: 'contractor', labelKey: 'settings.roles.contractor' },
];

// Permission-based role descriptions
const ROLE_DESCRIPTIONS = {
  admin: 'settings.roleDescAdmin',
  office: 'settings.roleDescOffice',
  driller: 'settings.roleDescDriller',
  pump_installer: 'settings.roleDescPumpInstaller',
  operator: 'settings.roleDescField',
};

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user, loadUser } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = ['admin', 'superadmin'].includes(user?.role);

  // Get initial tab from URL parameter or default to 'company'
  const initialTab = searchParams.get('tab') || 'company';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [company, setCompany] = useState({});
  const [counties, setCounties] = useState([]);
  const [users, setUsers] = useState([]);
  const [helpArticles, setHelpArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [contract, setContract] = useState({ title: '', content: '', is_active: true });
  const [contractSaving, setContractSaving] = useState(false);

  // Document Footers state
  const [footers, setFooters] = useState({ estimate_footer: '', invoice_footer: '', email_footer: '' });
  const [footersSaving, setFootersSaving] = useState(false);

  // Email Templates state (Alian: "me gustaría tener acceso para cambiar el wording")
  const [emailTemplates, setEmailTemplates] = useState({ invoice_subject: '', invoice_body: '', estimate_subject: '', estimate_body: '' });
  const [emailTemplatesSaving, setEmailTemplatesSaving] = useState(false);
  const [testSending, setTestSending] = useState({ invoice: false, estimate: false });

  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({ title: '', category: 'general', content: '', order: 0 });

  const [countyDialogOpen, setCountyDialogOpen] = useState(false);
  const [countyName, setCountyName] = useState('');

  // User dialog state
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'driller',
    phone: '',
    is_active: true,
    pay_type: 'none',
    pay_value: 0,
    default_job_category: '',
    contact_id: '',
    notification_preferences: {},
  });
  const [contacts, setContacts] = useState([]);
  const [notificationEvents, setNotificationEvents] = useState([]);
  const userFormSnapshotRef = useRef(null);

  // Logo state
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);

  // Contractor signature state (auto-stamped on official forms' Contractor field)
  const [sigSaving, setSigSaving] = useState(false);
  const sigCanvasRef = useRef(null);

  // Permissions matrix state (editable by admin/superadmin)
  const [permMatrix, setPermMatrix] = useState(null);
  const [permSaving, setPermSaving] = useState(false);
  const [permDirty, setPermDirty] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [compRes, countyRes, usersRes, helpRes, contractRes, footersRes, emailTplRes] = await Promise.all([
        api.get('/settings/company'),
        api.get('/settings/counties'),
        isAdmin ? api.get('/settings/users') : Promise.resolve({ data: [] }),
        api.get('/settings/help'),
        isAdmin ? api.get('/settings/contract') : Promise.resolve({ data: { title: '', content: '', is_active: true } }),
        api.get('/settings/footers').catch(() => ({ data: { estimate_footer: '', invoice_footer: '' } })),
        isAdmin ? api.get('/settings/email-templates').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
      ]);
      setCompany(compRes.data);
      setCounties(countyRes.data);
      setUsers(usersRes.data);
      setHelpArticles(helpRes.data);
      if (isAdmin) {
        api.get('/settings/notification-events').then(r => setNotificationEvents(r.data)).catch(() => {});
      }
      setContract({
        title: contractRes.data.title || '',
        content: contractRes.data.content || '',
        is_active: contractRes.data.is_active !== false
      });
      setFooters({
        estimate_footer: footersRes.data.estimate_footer || '',
        invoice_footer: footersRes.data.invoice_footer || '',
        email_footer: footersRes.data.email_footer || ''
      });
      setEmailTemplates({
        invoice_subject: emailTplRes.data.invoice_subject || '',
        invoice_body: emailTplRes.data.invoice_body || '',
        estimate_subject: emailTplRes.data.estimate_subject || '',
        estimate_body: emailTplRes.data.estimate_body || '',
      });
      // Check if company has a logo
      if (compRes.data.logo_url) {
        setLogoUrl(compRes.data.logo_url);
      }
      // Fetch permissions matrix if admin
      if (isAdmin) {
        try {
          const permRes = await api.get('/permissions');
          setPermMatrix(permRes.data);
          setPermDirty(false);
        } catch { /* ignore */ }
      }
    } catch { toast.error(t('settings.errorLoading')); }
    finally { setLoading(false); }
  }, [isAdmin, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveCompany = async () => {
    try {
      await api.put('/settings/company', company);
      toast.success(t('settings.companySaved'));
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
  };

  const saveContract = async () => {
    setContractSaving(true);
    try {
      const res = await api.put('/settings/contract', contract);
      setContract({
        title: res.data.title || '',
        content: res.data.content || '',
        is_active: res.data.is_active !== false
      });
      toast.success(t('settings.contractSaved'));
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
    finally { setContractSaving(false); }
  };

  const saveFooters = async () => {
    setFootersSaving(true);
    try {
      await api.put('/settings/footers', footers);
      toast.success(t('settings.footersSaved'));
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
    finally { setFootersSaving(false); }
  };

  const saveEmailTemplates = async () => {
    setEmailTemplatesSaving(true);
    try {
      const res = await api.put('/settings/email-templates', emailTemplates);
      setEmailTemplates(res.data);
      toast.success('Email templates saved');
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
    finally { setEmailTemplatesSaving(false); }
  };

  const sendTestTemplateEmail = async (docType) => {
    setTestSending(prev => ({ ...prev, [docType]: true }));
    try {
      const res = await api.post(`/emails/test-template/${docType}`);
      toast.success(`Test email sent to ${(res.data.to || []).join(', ') || 'your inbox'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send test email'); }
    finally { setTestSending(prev => ({ ...prev, [docType]: false })); }
  };

  // Toggle a single View/Create/Edit/Delete/Cancel cell in the permissions matrix
  const togglePermCell = (role, module, action, checked) => {
    setPermMatrix(prev => {
      if (!prev) return prev;
      const next = { ...prev, permissions: { ...prev.permissions } };
      next.permissions[role] = { ...next.permissions[role] };
      next.permissions[role][module] = { ...next.permissions[role][module], [action]: checked };
      return next;
    });
    setPermDirty(true);
  };

  const savePermissions = async () => {
    if (!permMatrix) return;
    setPermSaving(true);
    try {
      const res = await api.put('/permissions', { permissions: permMatrix.permissions });
      setPermMatrix(prev => ({ ...prev, permissions: res.data.permissions }));
      setPermDirty(false);
      toast.success(t('settings.permissionsSaved'));
      // Refresh our own session in case the edited role was our own (admin) —
      // buttons across the app should reflect the new permissions right away.
      loadUser();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.savingError')); }
    finally { setPermSaving(false); }
  };


  const addCounty = async (e) => {
    e.preventDefault();
    if (!countyName.trim()) return;
    try {
      await api.post('/settings/counties', { name: countyName, state: 'FL' });
      toast.success(t('settings.countyAdded'));
      setCountyDialogOpen(false);
      setCountyName('');
      invalidateCountiesCache();
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.generic')); }
  };

  const deleteCounty = async (id) => {
    try { await api.delete(`/settings/counties/${id}`); toast.success(t('settings.countyDeleted')); invalidateCountiesCache(); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  // User management functions
  const fetchContacts = async () => {
    if (contacts.length > 0) return;
    try { const res = await api.get('/contacts'); setContacts(Array.isArray(res.data) ? res.data : (res.data.items || [])); }
    catch { /* non-blocking */ }
  };

  const openUserDialog = (existing = null) => {
    fetchContacts();
    let initial;
    if (existing) {
      setEditingUserId(existing.id);
      initial = {
        email: existing.email || '',
        password: '', // password no se precarga; vacio = no cambiar
        first_name: existing.first_name || '',
        last_name: existing.last_name || '',
        role: existing.role || 'driller',
        phone: existing.phone || '',
        is_active: existing.is_active !== false,
        pay_type: existing.pay_type || 'none',
        pay_value: existing.pay_value || 0,
        default_job_category: existing.default_job_category || '',
        contact_id: existing.contact_id || '',
        notification_preferences: existing.notification_preferences || {},
      };
    } else {
      setEditingUserId(null);
      initial = {
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role: 'driller',
        phone: '',
        is_active: true,
        pay_type: 'none',
        pay_value: 0,
        default_job_category: '',
        contact_id: '',
        notification_preferences: {},
      };
    }
    setUserForm(initial);
    userFormSnapshotRef.current = JSON.stringify(initial);
    setUserDialogOpen(true);
  };

  const isUserFormDirty = () => userFormSnapshotRef.current !== null && JSON.stringify(userForm) !== userFormSnapshotRef.current;

  const closeUserDialog = () => {
    if (isUserFormDirty() && !window.confirm('You have unsaved changes. Discard them?')) {
      return;
    }
    userFormSnapshotRef.current = null;
    setUserDialogOpen(false);
    setEditingUserId(null);
  };

  const saveUser = async (e) => {
    e.preventDefault();
    // Validacion: en CREATE password es obligatorio; en EDIT es opcional
    if (!userForm.email.trim() || !userForm.first_name.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    if (!editingUserId && !userForm.password.trim()) {
      toast.error(t('errors.requiredField'));
      return;
    }
    try {
      if (editingUserId) {
        // Solo enviar campos relevantes y omitir password si vacio
        const payload = {
          first_name: userForm.first_name,
          last_name: userForm.last_name,
          email: userForm.email,
          role: userForm.role,
          phone: userForm.phone,
          is_active: userForm.is_active,
          pay_type: userForm.pay_type || 'none',
          pay_value: parseFloat(userForm.pay_value) || 0,
          default_job_category: userForm.default_job_category || '',
          contact_id: userForm.contact_id || '',
          notification_preferences: userForm.notification_preferences || {},
        };
        if (userForm.password.trim()) {
          payload.new_password = userForm.password;
        }
        await api.put(`/auth/users/${editingUserId}`, payload);
        toast.success(t('settings.userUpdated', { defaultValue: 'User updated' }));
      } else {
        await api.post('/auth/register', { ...userForm, pay_value: parseFloat(userForm.pay_value) || 0 });
        toast.success(t('settings.userCreated'));
      }
      userFormSnapshotRef.current = null;
      setUserDialogOpen(false);
      setEditingUserId(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.generic'));
    }
  };

  const openArticleDialog = (article = null) => {
    setEditingArticle(article);
    setArticleForm(article ? { title: article.title, category: article.category, content: article.content, order: article.order || 0 } : { title: '', category: 'general', content: '', order: 0 });
    setHelpDialogOpen(true);
  };

  // Logo upload handler
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo demasiado grande (max 5MB)');
      return;
    }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoUrl(res.data.logo_url);
      toast.success(t('settings.logoUploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error subiendo logo');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoRemove = async () => {
    try {
      await api.delete('/settings/logo');
      setLogoUrl(null);
      toast.success(t('settings.logoRemoved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error eliminando logo');
    }
  };

  const saveContractorSignature = async () => {
    if (sigCanvasRef.current?.isEmpty()) {
      toast.error('Dibuje su firma antes de guardar');
      return;
    }
    setSigSaving(true);
    try {
      // NOTE: getTrimmedCanvas() throws under CRA/webpack5 (broken ESM/CJS
      // interop of the bundled `trim-canvas` dep in react-signature-canvas
      // 1.1.0-alpha.2) — getCanvas() avoids the buggy trim step.
      const signatureImage = sigCanvasRef.current.getCanvas().toDataURL('image/png');
      const res = await api.put('/settings/company', { contractor_signature_image: signatureImage });
      setCompany(prev => ({ ...prev, contractor_signature_image: res.data.contractor_signature_image }));
      toast.success('Firma guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setSigSaving(false);
    }
  };

  const clearContractorSignature = async () => {
    try {
      await api.put('/settings/company', { contractor_signature_image: '' });
      setCompany(prev => ({ ...prev, contractor_signature_image: '' }));
      toast.success('Firma eliminada');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.deletingError'));
    }
  };

  const saveArticle = async (e) => {
    e.preventDefault();
    if (!articleForm.title.trim()) { toast.error(t('errors.requiredField')); return; }
    try {
      if (editingArticle) {
        await api.put(`/settings/help/${editingArticle.id}`, articleForm);
        toast.success(t('settings.articleUpdated'));
      } else {
        await api.post('/settings/help', articleForm);
        toast.success(t('settings.articleCreated'));
      }
      setHelpDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('errors.generic')); }
  };

  const deleteArticle = async (id) => {
    try { await api.delete(`/settings/help/${id}`); toast.success(t('settings.articleDeleted')); fetchData(); }
    catch { toast.error(t('errors.deletingError')); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-[#003366]/20 border-t-[#003366] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="settings-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 font-['Barlow'] tracking-tight flex items-center gap-2">
          <Settings className="w-6 h-6 text-[#3399FF]" /> {t('settings.title')}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t('settings.subtitle')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-gray-200 h-10">
          <TabsTrigger value="company" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
            <Building className="w-3.5 h-3.5" /> {t('settings.tabCompany')}
          </TabsTrigger>
          <TabsTrigger value="counties" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
            <MapPin className="w-3.5 h-3.5" /> {t('settings.tabCounties')}
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="users" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <Users className="w-3.5 h-3.5" /> {t('settings.tabUsers')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="contract" data-testid="settings-contract-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <FileText className="w-3.5 h-3.5" /> {t('settings.tabContract')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="footers" data-testid="settings-footers-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <Hash className="w-3.5 h-3.5" /> {t('settings.tabFooters')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="emailTemplates" data-testid="settings-email-templates-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <Mail className="w-3.5 h-3.5" /> Email Templates
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="permissions" data-testid="settings-permissions-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> {t('settings.tabPermissions')}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="jobStatus" data-testid="settings-job-status-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <Layers className="w-3.5 h-3.5" /> Job Status
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="estimateStatus" data-testid="settings-estimate-status-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <ListChecks className="w-3.5 h-3.5" /> Estimate Status
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="phone" data-testid="settings-phone-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <PhoneCall className="w-3.5 h-3.5" /> Phone
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="maintenance" data-testid="settings-maintenance-tab" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Maintenance
            </TabsTrigger>
          )}

          <TabsTrigger value="help" className="text-xs data-[state=active]:bg-[#003366] data-[state=active]:text-white gap-1.5">
            <HelpCircle className="w-3.5 h-3.5" /> {t('settings.tabHelp')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <Card className="bg-white border-gray-100">
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 space-y-2">
                  <Label>{t('settings.companyName')}</Label>
                  <Input data-testid="company-name" value={company.company_name || ''} onChange={e => setCompany({ ...company, company_name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.phone')}</Label>
                  <Input value={company.phone || ''} onChange={e => setCompany({ ...company, phone: formatPhoneNumber(e.target.value) })} placeholder="(786) 510-4512" />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.email')}</Label>
                  <Input value={company.email || ''} onChange={e => setCompany({ ...company, email: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.website')}</Label>
                  <Input value={company.website || ''} onChange={e => setCompany({ ...company, website: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.licenseNumber')}</Label>
                  <Input value={company.license_number || ''} onChange={e => setCompany({ ...company, license_number: e.target.value })} />
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label>{t('settings.companyAddress')}</Label>
                  <Input value={company.address || ''} onChange={e => setCompany({ ...company, address: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('jobDetail.city')}</Label>
                  <Input value={company.city || ''} onChange={e => setCompany({ ...company, city: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.zipCode')}</Label>
                  <Input value={company.zip_code || ''} onChange={e => setCompany({ ...company, zip_code: e.target.value })} />
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">{t('settings.brandColors')}</p>
                <div className="flex gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs">{t('settings.primaryColor')}</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={company.primary_color || '#003366'} onChange={e => setCompany({ ...company, primary_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                      <Input value={company.primary_color || '#003366'} onChange={e => setCompany({ ...company, primary_color: e.target.value })} className="w-28 font-mono text-sm" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">{t('settings.secondaryColor')}</Label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={company.secondary_color || '#3399FF'} onChange={e => setCompany({ ...company, secondary_color: e.target.value })} className="w-10 h-10 rounded cursor-pointer" />
                      <Input value={company.secondary_color || '#3399FF'} onChange={e => setCompany({ ...company, secondary_color: e.target.value })} className="w-28 font-mono text-sm" />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Company Logo Section */}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Image className="w-5 h-5 text-[#003366]" />
                    <p className="text-sm font-semibold text-gray-700">{t('settings.logoUpload')}</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">{t('settings.logoUploadDesc')}</p>
                  
                  <div className="flex items-start gap-6">
                    {/* Logo Preview */}
                    <div data-testid="logo-preview" className="w-28 h-28 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden bg-gray-50">
                      {logoUrl ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}?t=${Date.now()}`}
                          alt="Company Logo" 
                          className="w-full h-full object-contain p-1"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="text-center">
                          <Image className="w-8 h-8 text-gray-300 mx-auto mb-1" />
                          <span className="text-[10px] text-gray-400">{t('settings.noLogo')}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Controls */}
                    <div className="flex flex-col gap-2">
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="logo-file-input"
                      />
                      <Button
                        data-testid="upload-logo-btn"
                        size="sm"
                        variant="outline"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                        className="border-[#003366]/20 text-[#003366]"
                      >
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                        {logoUploading ? 'Subiendo...' : t('settings.logoUploadBtn')}
                      </Button>
                      {logoUrl && (
                        <Button
                          data-testid="remove-logo-btn"
                          size="sm"
                          variant="ghost"
                          onClick={handleLogoRemove}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          {t('settings.logoRemoveBtn')}
                        </Button>
                      )}
                      <p className="text-[10px] text-gray-400 max-w-[200px]">PNG, JPG, SVG. Max 5MB.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contractor Signature — se estampa sola en el campo "Signature
                  of Contractor" de las formas oficiales (Permit Application,
                  etc.) cuando ese campo esta marcado como "Contractor" en el
                  Form Designer. Al cliente nunca se le pide esta firma. */}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <PenLine className="w-5 h-5 text-[#003366]" />
                    <p className="text-sm font-semibold text-gray-700">Firma del Contratista</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">
                    Se guarda una sola vez y se estampa automáticamente en el campo de firma del contratista de las formas oficiales enviadas al cliente para firma online.
                  </p>

                  {company.contractor_signature_image ? (
                    <div className="flex items-start gap-4">
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="contractor-signature-preview">
                        <img src={company.contractor_signature_image} alt="Firma del contratista" className="max-h-16" />
                      </div>
                      <Button size="sm" variant="ghost" onClick={clearContractorSignature} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid="remove-contractor-signature-btn">
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Eliminar y rehacer
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 max-w-md">
                      <div className="relative border-2 border-gray-200 rounded-xl overflow-hidden bg-gray-50 hover:border-[#003366]/30 transition-colors">
                        <SignatureCanvas
                          ref={sigCanvasRef}
                          penColor="#003366"
                          canvasProps={{ className: 'w-full', style: { height: '120px', touchAction: 'none' } }}
                          backgroundColor="rgb(249,250,251)"
                        />
                        <button
                          type="button"
                          onClick={() => sigCanvasRef.current?.clear()}
                          className="absolute top-2 right-2 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 bg-white/80 px-2 py-1 rounded-lg border border-gray-200"
                          data-testid="clear-contractor-signature-btn"
                        >
                          <RotateCcw className="w-3 h-3" /> Limpiar
                        </button>
                      </div>
                      <Button size="sm" onClick={saveContractorSignature} disabled={sigSaving} className="bg-[#003366] hover:bg-[#004080] text-white" data-testid="save-contractor-signature-btn">
                        {sigSaving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />} Guardar Firma
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Auto Numbering Settings */}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Hash className="w-5 h-5 text-[#003366]" />
                    <p className="text-sm font-semibold text-gray-700">{t('settings.autoNumbering')}</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">{t('settings.autoNumberingDesc')}</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {/* Invoices */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('settings.invoiceNumbering')}</Label>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-[10px] text-gray-400">{t('settings.prefix')}</Label>
                            <Input 
                              value={company.invoice_prefix || 'INV'} 
                              onChange={e => setCompany({ ...company, invoice_prefix: e.target.value })} 
                              className="h-8 text-sm font-mono"
                              placeholder="INV"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-[10px] text-gray-400">{t('settings.startNumber')}</Label>
                            <Input 
                              type="number"
                              value={company.invoice_start_number || 1} 
                              onChange={e => setCompany({ ...company, invoice_start_number: parseInt(e.target.value) || 1 })} 
                              className="h-8 text-sm"
                              min="1"
                            />
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-400">{t('settings.example')}: {company.invoice_prefix || 'INV'}-2026-{String(company.invoice_start_number || 1).padStart(3, '0')}</p>
                      </div>
                    </div>
                    
                    {/* Estimates */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('settings.estimateNumbering')}</Label>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-[10px] text-gray-400">{t('settings.prefix')}</Label>
                            <Input 
                              value={company.estimate_prefix || 'EST'} 
                              onChange={e => setCompany({ ...company, estimate_prefix: e.target.value })} 
                              className="h-8 text-sm font-mono"
                              placeholder="EST"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-[10px] text-gray-400">{t('settings.startNumber')}</Label>
                            <Input 
                              type="number"
                              value={company.estimate_start_number || 1} 
                              onChange={e => setCompany({ ...company, estimate_start_number: parseInt(e.target.value) || 1 })} 
                              className="h-8 text-sm"
                              min="1"
                            />
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-400">{t('settings.example')}: {company.estimate_prefix || 'EST'}-2026-{String(company.estimate_start_number || 1).padStart(3, '0')}</p>
                      </div>
                    </div>
                    
                    {/* Jobs */}
                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                      <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('settings.jobNumbering')}</Label>
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-[10px] text-gray-400">{t('settings.prefix')}</Label>
                            <Input 
                              value={company.job_prefix || 'JOB'} 
                              onChange={e => setCompany({ ...company, job_prefix: e.target.value })} 
                              className="h-8 text-sm font-mono"
                              placeholder="JOB"
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-[10px] text-gray-400">{t('settings.startNumber')}</Label>
                            <Input 
                              type="number"
                              value={company.job_start_number || 1} 
                              onChange={e => setCompany({ ...company, job_start_number: parseInt(e.target.value) || 1 })} 
                              className="h-8 text-sm"
                              min="1"
                            />
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-400">{t('settings.example')}: {company.job_prefix || 'JOB'}-2026-{String(company.job_start_number || 1).padStart(3, '0')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Credit Card Surcharge (TEMA 2.1) — lives next to the other operating
                  numbers, per Alian: "menos es mejor", no new tab. */}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-5 h-5 text-[#003366]" />
                    <p className="text-sm font-semibold text-gray-700">Credit card surcharge</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">Percentage passed on to the customer when a payment is collected with Credit Card (matches the "processing fee" already promised in the estimate PDF and contract).</p>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 max-w-xs">
                    <Label className="text-xs font-semibold text-gray-600 mb-2 block">Credit card surcharge (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      data-testid="card-surcharge-rate-input"
                      value={company.credit_card_surcharge_rate ?? 4}
                      onChange={e => setCompany({ ...company, credit_card_surcharge_rate: Math.max(0, Math.min(10, parseFloat(e.target.value) || 0)) })}
                      className="h-9 text-sm w-24"
                    />
                  </div>
                </div>
              )}
              {/* Scheduling Rules */}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarDays className="w-5 h-5 text-[#003366]" />
                    <p className="text-sm font-semibold text-gray-700">{t('settings.schedulingRules', { defaultValue: 'Scheduling Rules' })}</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">{t('settings.schedulingRulesDesc', { defaultValue: 'Max number of Well Construction jobs that can be scheduled on the same day. Admins can schedule beyond the limit after confirming; other roles are blocked.' })}</p>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 max-w-xs">
                    <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('settings.wellsPerDay', { defaultValue: 'Well Construction per day (max)' })}</Label>
                    <Input
                      type="number"
                      min="1"
                      data-testid="wells-per-day-input"
                      value={company.well_construction_max_per_day ?? 1}
                      onChange={e => setCompany({ ...company, well_construction_max_per_day: Math.max(1, parseInt(e.target.value) || 1) })}
                      className="h-9 text-sm w-24"
                    />
                  </div>
                </div>
              )}
              {/* Address Search — Service Area (TEMA: geocode fix) */}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-5 h-5 text-[#003366]" />
                    <p className="text-sm font-semibold text-gray-700">{t('settings.serviceArea', { defaultValue: 'Address Search — Service Area' })}</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">{t('settings.serviceAreaDesc', { defaultValue: "Counties and radius used to rank nearby addresses first in the search. Far addresses are never hidden — they're just listed last, with their distance shown." })}</p>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                    <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('settings.maxServiceRadius', { defaultValue: 'Search radius (miles)' })}</Label>
                    <Input
                      type="number"
                      min="10"
                      max="1000"
                      data-testid="service-radius-input"
                      value={company.max_service_radius_miles ?? 250}
                      onChange={e => setCompany({ ...company, max_service_radius_miles: Math.max(10, parseInt(e.target.value) || 250) })}
                      className="h-9 text-sm w-28 mb-4"
                    />
                    <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('settings.operatingCounties', { defaultValue: 'Counties within the service area' })}</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="operating-counties-list">
                      {counties.map((c) => {
                        const checked = (company.operating_counties || []).includes(c.name);
                        return (
                          <label
                            key={c.id || c.name}
                            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                            data-testid={`operating-county-checkbox-${c.name.replace(/[^a-zA-Z]/g, '')}`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(val) => {
                                const current = company.operating_counties || [];
                                const next = val ? [...current, c.name] : current.filter(n => n !== c.name);
                                setCompany({ ...company, operating_counties: next });
                              }}
                            />
                            {c.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
              {/* Session / Inactivity Auto-Logout (TEMA D) */}
              {isAdmin && (
                <div className="border-t pt-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Timer className="w-5 h-5 text-[#003366]" />
                    <p className="text-sm font-semibold text-gray-700">{t('settings.inactivityTimeout')}</p>
                  </div>
                  <p className="text-xs text-gray-500 mb-4">{t('settings.inactivityTimeoutDesc')}</p>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 max-w-xs">
                    <Label className="text-xs font-semibold text-gray-600 mb-2 block">{t('settings.inactivityTimeoutMinutes')}</Label>
                    <Input
                      type="number"
                      min="5"
                      max="120"
                      data-testid="inactivity-timeout-input"
                      value={company.inactivity_timeout_minutes ?? 15}
                      onChange={e => setCompany({ ...company, inactivity_timeout_minutes: Math.max(5, parseInt(e.target.value) || 15) })}
                      className="h-9 text-sm w-24"
                    />
                  </div>
                </div>
              )}
              {isAdmin && (
                <div className="flex justify-end pt-2">
                  <Button data-testid="save-company-btn" onClick={saveCompany} className="bg-[#003366] hover:bg-[#004080] text-white">
                    <Save className="w-4 h-4 mr-2" /> {t('settings.saveChanges')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="counties">
          <Card className="bg-white border-gray-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-gray-700">{t('settings.operationalCounties')}</p>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => setCountyDialogOpen(true)} className="border-[#003366]/20 text-[#003366]">
                    <Plus className="w-3.5 h-3.5 mr-1" /> {t('settings.addCounty')}
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {counties.map((c, i) => (
                  <div key={c.id || i} className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                    <span className="text-sm font-medium text-gray-700">{c.name}</span>
                    {isAdmin && c.id && (
                      <Button variant="ghost" size="icon" onClick={() => deleteCounty(c.id)} className="h-6 w-6 text-red-400 hover:text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users">
            <Card className="bg-white border-gray-100">
              <CardContent className="p-0">
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-700">{t('settings.teamMembers')}</p>
                  <Button size="sm" onClick={openUserDialog} className="bg-[#003366] hover:bg-[#004080] text-white">
                    <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('settings.addUser')}
                  </Button>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50/80 border-b border-gray-100">
                      <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('settings.usersTable.name')}</th>
                      <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('settings.usersTable.email')}</th>
                      <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('settings.usersTable.role')}</th>
                      <th className="text-left text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3">{t('settings.usersTable.status')}</th>
                      <th className="text-right text-xs uppercase tracking-wider text-gray-500 font-semibold px-5 py-3 w-24">{t('common.actions', { defaultValue: 'Actions' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="px-5 py-3 text-sm font-medium text-gray-900">{u.first_name} {u.last_name}</td>
                        <td className="px-5 py-3 text-sm text-gray-600">{u.email}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            u.role === 'superadmin' ? 'bg-purple-100 text-purple-700' :
                            u.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                            u.role === 'office' ? 'bg-green-100 text-green-700' :
                            u.role === 'driller' ? 'bg-amber-100 text-amber-700' :
                            u.role === 'pump_installer' ? 'bg-cyan-100 text-cyan-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {t(`settings.roles.${u.role}`, { defaultValue: u.role })}
                          </span>
                        </td>
                        <td className="px-5 py-3"><StatusBadge status={u.is_active ? 'active' : 'cancelled'} /></td>
                        <td className="px-5 py-3 text-right">
                          <Button
                            data-testid={`edit-user-btn-${u.id}`}
                            size="sm"
                            variant="ghost"
                            onClick={() => openUserDialog(u)}
                            className="h-8 px-2 text-gray-600 hover:text-[#003366] hover:bg-blue-50"
                            title={t('common.edit', { defaultValue: 'Edit' })}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="contract">
            <Card className="bg-white border-gray-100">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">{t('settings.contractTitle')}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t('settings.contractSubtitle')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="contract-active" className="text-xs text-gray-500">{t('settings.contractActive')}</Label>
                    <Switch
                      id="contract-active"
                      data-testid="contract-active-toggle"
                      checked={contract.is_active}
                      onCheckedChange={(checked) => setContract({ ...contract, is_active: checked })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.contractTitleLabel')}</Label>
                  <Input
                    data-testid="contract-title-input"
                    value={contract.title}
                    onChange={e => setContract({ ...contract, title: e.target.value })}
                    placeholder="Water Well Drilling Agreement"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('settings.contractContent')}</Label>
                  <Textarea
                    data-testid="contract-content-input"
                    value={contract.content}
                    onChange={e => setContract({ ...contract, content: e.target.value })}
                    placeholder={t('settings.contractPlaceholder')}
                    rows={18}
                    className="font-mono text-sm leading-relaxed"
                  />
                  <p className="text-xs text-gray-400">{contract.content?.length || 0} {t('settings.characters')}</p>
                </div>
                <div className="flex justify-end pt-2">
                  <Button
                    data-testid="save-contract-btn"
                    onClick={saveContract}
                    disabled={contractSaving}
                    className="bg-[#003366] hover:bg-[#004080] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {contractSaving ? t('settings.contractSaving') : t('settings.contractSaveBtn')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* DOCUMENT FOOTERS TAB */}
        {isAdmin && (
          <TabsContent value="footers">
            <Card className="bg-white border-gray-100">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">{t('settings.footersTitle')}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t('settings.footersSubtitle')}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>

                {/* Estimate Footer */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">{t('settings.estimateFooter')}</Label>
                      <p className="text-xs text-gray-400">{t('settings.estimateFooterDesc')}</p>
                    </div>
                  </div>
                  <Textarea
                    data-testid="estimate-footer-input"
                    value={footers.estimate_footer}
                    onChange={e => setFooters({ ...footers, estimate_footer: e.target.value })}
                    placeholder={t('settings.estimateFooterPlaceholder')}
                    rows={8}
                    className="font-mono text-sm leading-relaxed"
                  />
                  <p className="text-xs text-gray-400">{footers.estimate_footer?.length || 0} {t('settings.characters')}</p>
                </div>

                {/* Invoice Footer */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <Hash className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">{t('settings.invoiceFooter')}</Label>
                      <p className="text-xs text-gray-400">{t('settings.invoiceFooterDesc')}</p>
                    </div>
                  </div>
                  <Textarea
                    data-testid="invoice-footer-input"
                    value={footers.invoice_footer}
                    onChange={e => setFooters({ ...footers, invoice_footer: e.target.value })}
                    placeholder={t('settings.invoiceFooterPlaceholder')}
                    rows={8}
                    className="font-mono text-sm leading-relaxed"
                  />
                  <p className="text-xs text-gray-400">{footers.invoice_footer?.length || 0} {t('settings.characters')}</p>
                </div>

                {/* Email Footer */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <Label className="text-sm font-semibold">Email Footer</Label>
                      <p className="text-xs text-gray-400">Shown at the bottom of estimate &amp; invoice emails. Can be overridden when sending.</p>
                    </div>
                  </div>
                  <Textarea
                    data-testid="email-footer-input"
                    value={footers.email_footer}
                    onChange={e => setFooters({ ...footers, email_footer: e.target.value })}
                    placeholder="Footer text shown in outbound emails..."
                    rows={6}
                    className="font-mono text-sm leading-relaxed"
                  />
                  <p className="text-xs text-gray-400">{footers.email_footer?.length || 0} {t('settings.characters')}</p>
                </div>

                {/* Preview Section */}
                <div className="border-t pt-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">{t('settings.footerPreview')}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 mb-2">{t('settings.estimatePreview')}</p>
                      <p className="text-xs text-gray-500 whitespace-pre-line">{footers.estimate_footer || t('settings.noFooterSet')}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 mb-2">{t('settings.invoicePreview')}</p>
                      <p className="text-xs text-gray-500 whitespace-pre-line">{footers.invoice_footer || t('settings.noFooterSet')}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-semibold text-gray-600 mb-2">Email</p>
                      <p className="text-xs text-gray-500 whitespace-pre-line">{footers.email_footer || t('settings.noFooterSet')}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    data-testid="save-footers-btn"
                    onClick={saveFooters}
                    disabled={footersSaving}
                    className="bg-[#003366] hover:bg-[#004080] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {footersSaving ? t('settings.footersSaving') : t('settings.footersSaveBtn')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* EMAIL TEMPLATES TAB */}
        {isAdmin && (
          <TabsContent value="emailTemplates">
            <Card className="bg-white border-gray-100">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Email Templates</p>
                    <p className="text-xs text-gray-400 mt-0.5">Wording of the Invoice &amp; Estimate emails your customers receive. Line items are shown automatically wherever you place {'{items_table}'}.</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Invoice Email */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Invoice Email</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => sendTestTemplateEmail('invoice')} disabled={testSending.invoice} data-testid="send-test-invoice-email-btn">
                      {testSending.invoice ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                      Send test email to me
                    </Button>
                  </div>
                  <Input
                    data-testid="invoice-email-subject-input"
                    value={emailTemplates.invoice_subject}
                    onChange={e => setEmailTemplates({ ...emailTemplates, invoice_subject: e.target.value })}
                    placeholder="Invoice {invoice_number} - Blue Well Drilling"
                  />
                  <Textarea
                    data-testid="invoice-email-body-input"
                    value={emailTemplates.invoice_body}
                    onChange={e => setEmailTemplates({ ...emailTemplates, invoice_body: e.target.value })}
                    rows={8}
                    className="font-mono text-sm leading-relaxed"
                  />
                  <p className="text-[11px] text-gray-400">
                    Variables: {'{customer_name} {invoice_number} {total} {amount_due} {due_date} {company_phone} {pay_link} {items_table}'}
                  </p>
                </div>

                {/* Estimate Email */}
                <div className="space-y-3 border-t pt-5">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Estimate Email</Label>
                    <Button type="button" size="sm" variant="outline" onClick={() => sendTestTemplateEmail('estimate')} disabled={testSending.estimate} data-testid="send-test-estimate-email-btn">
                      {testSending.estimate ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                      Send test email to me
                    </Button>
                  </div>
                  <Input
                    data-testid="estimate-email-subject-input"
                    value={emailTemplates.estimate_subject}
                    onChange={e => setEmailTemplates({ ...emailTemplates, estimate_subject: e.target.value })}
                    placeholder="Estimate {estimate_number} - Blue Well Drilling"
                  />
                  <Textarea
                    data-testid="estimate-email-body-input"
                    value={emailTemplates.estimate_body}
                    onChange={e => setEmailTemplates({ ...emailTemplates, estimate_body: e.target.value })}
                    rows={8}
                    className="font-mono text-sm leading-relaxed"
                  />
                  <p className="text-[11px] text-gray-400">
                    Variables: {'{customer_name} {estimate_number} {total} {company_phone} {approve_link} {items_table}'}
                  </p>
                </div>

                <div className="flex justify-end pt-2">
                  <Button
                    data-testid="save-email-templates-btn"
                    onClick={saveEmailTemplates}
                    disabled={emailTemplatesSaving}
                    className="bg-[#003366] hover:bg-[#004080] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {emailTemplatesSaving ? 'Saving...' : 'Save Templates'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* PERMISSIONS TAB */}
        {isAdmin && (
          <TabsContent value="permissions">
            <Card className="bg-white border-gray-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-sm font-semibold text-gray-700">{t('settings.permissionsTitle')}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t('settings.permissionsSubtitle')}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                </div>

                {permMatrix ? (
                  <div className="overflow-x-auto">
                    {/* One table per role */}
                    {['admin', 'office', 'driller', 'pump_installer', 'operator'].map(role => {
                      const rolePerms = permMatrix.permissions[role] || {};
                      const modules = permMatrix.modules || [];
                      const roleColors = {
                        admin: 'bg-blue-100 text-blue-700 border-blue-200',
                        office: 'bg-green-100 text-green-700 border-green-200',
                        driller: 'bg-amber-100 text-amber-700 border-amber-200',
                        pump_installer: 'bg-cyan-100 text-cyan-700 border-cyan-200',
                        operator: 'bg-gray-100 text-gray-700 border-gray-200',
                      };
                      return (
                        <div key={role} className="mb-6" data-testid={`perm-role-${role}`}>
                          <div className="flex items-center gap-2 mb-3">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${roleColors[role] || 'bg-gray-100 text-gray-700'}`}>
                              {t(`settings.roles.${role}`, { defaultValue: role })}
                            </span>
                            <span className="text-[10px] text-gray-400">
                              {role === 'admin' && t('settings.roleDescAdmin')}
                              {role === 'office' && t('settings.roleDescOffice')}
                              {role === 'driller' && t('settings.roleDescDriller')}
                              {role === 'pump_installer' && t('settings.roleDescPumpInstaller')}
                              {role === 'operator' && t('settings.roleDescField', { defaultValue: 'Standard operations access' })}
                            </span>
                          </div>
                          <table className="w-full text-xs border border-gray-100 rounded-lg overflow-hidden">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="text-left px-3 py-2 font-semibold text-gray-500 w-36">{t('settings.permModule')}</th>
                                <th className="text-center px-2 py-2 font-semibold text-gray-500 w-16">{t('settings.permView')}</th>
                                <th className="text-center px-2 py-2 font-semibold text-gray-500 w-16">{t('settings.permCreate')}</th>
                                <th className="text-center px-2 py-2 font-semibold text-gray-500 w-16">{t('settings.permEdit')}</th>
                                <th className="text-center px-2 py-2 font-semibold text-gray-500 w-16">{t('settings.permDelete')}</th>
                                <th className="text-center px-2 py-2 font-semibold text-gray-500 w-16">{t('settings.permCancel')}</th>
                                <th className="text-center px-2 py-2 font-semibold text-gray-500 w-20">{t('settings.permChangeStatus', { defaultValue: 'Change Status' })}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {modules.map((mod, mi) => {
                                const mp = rolePerms[mod];
                                if (!mp || typeof mp === 'boolean') return null;
                                // An admin can never remove their own access to Settings/Users
                                // (locked, forced ON) — otherwise they could lock themselves out.
                                return (
                                  <tr key={mod} className={mi % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                    <td className="px-3 py-2 font-medium text-gray-700">
                                      {t(`settings.permModules.${mod}`, { defaultValue: mod })}
                                    </td>
                                    {['view', 'create', 'edit', 'delete', 'cancel', 'change_status'].map(action => {
                                      if (mp[action] === undefined) {
                                        return <td key={action} className="text-center px-2 py-2"><span className="text-gray-200">-</span></td>;
                                      }
                                      const isLocked = role === 'admin' && ['settings', 'users'].includes(mod) && ['view', 'edit'].includes(action);
                                      return (
                                        <td key={action} className="text-center px-2 py-2">
                                          <TooltipProvider delayDuration={200}>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="inline-flex items-center justify-center">
                                                  <Checkbox
                                                    data-testid={`perm-checkbox-${role}-${mod}-${action}`}
                                                    checked={!!mp[action]}
                                                    disabled={isLocked}
                                                    onCheckedChange={(checked) => togglePermCell(role, mod, action, !!checked)}
                                                    className={isLocked ? 'opacity-70 cursor-not-allowed' : ''}
                                                  />
                                                </span>
                                              </TooltipTrigger>
                                              {isLocked && (
                                                <TooltipContent side="top" className="text-xs max-w-[200px]">
                                                  {t('settings.permissionsLockedTooltip')}
                                                </TooltipContent>
                                              )}
                                            </Tooltip>
                                          </TooltipProvider>
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}

                    <div className="sticky bottom-0 -mx-6 -mb-6 mt-2 px-6 py-3 bg-white/95 backdrop-blur border-t border-gray-100 flex items-center justify-between">
                      <p className="text-[11px] text-gray-400">
                        {permDirty ? t('settings.permissionsUnsavedHint') : t('settings.permissionsUpToDateHint')}
                      </p>
                      <Button
                        data-testid="save-permissions-btn"
                        onClick={savePermissions}
                        disabled={permSaving || !permDirty}
                        size="sm"
                        className="bg-[#003366] hover:bg-[#004080] text-white"
                      >
                        {permSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        {permSaving ? t('settings.permissionsSaving') : t('settings.permissionsSaveBtn')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Loading permissions...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* JOB STATUS TAB */}
        {isAdmin && (
          <TabsContent value="jobStatus">
            <JobStatusSettings />
          </TabsContent>
        )}

        {/* ESTIMATE STATUS TAB — Fix B (2026-08-21) */}
        {isAdmin && (
          <TabsContent value="estimateStatus">
            <EstimateStatusSettings />
          </TabsContent>
        )}

        {/* PHONE / CALL RECORDING TAB */}
        {isAdmin && (
          <TabsContent value="phone">
            <CallRecordingSettings />
          </TabsContent>
        )}

        {/* MAINTENANCE / GO-LIVE TAB */}
        {isAdmin && (
          <TabsContent value="maintenance">
            <MaintenanceTab users={users} />
          </TabsContent>
        )}

        <TabsContent value="help">
          <Card className="bg-white border-gray-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-gray-700">{t('settings.helpArticles')}</p>
                {isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => openArticleDialog()} className="border-[#003366]/20 text-[#003366]">
                    <Plus className="w-3.5 h-3.5 mr-1" /> {t('settings.newArticle')}
                  </Button>
                )}
              </div>
              {helpArticles.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">{t('settings.noArticles')}</p>
              ) : (
                <div className="space-y-3">
                  {helpArticles.map(a => (
                    <div key={a.id} className="p-4 rounded-lg bg-gray-50 border border-gray-100">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <HelpCircle className="w-4 h-4 text-[#3399FF]" />
                            <h4 className="text-sm font-semibold text-gray-900">{a.title}</h4>
                            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded capitalize">{a.category}</span>
                          </div>
                          <p className="text-sm text-gray-600 whitespace-pre-line">{a.content}</p>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1 ml-3">
                            <Button variant="ghost" size="icon" onClick={() => openArticleDialog(a)} className="h-7 w-7"><Pencil className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteArticle(a.id)} className="h-7 w-7 text-red-400"><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      <Dialog open={countyDialogOpen} onOpenChange={setCountyDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t('settings.addCountyTitle')}</DialogTitle></DialogHeader>
          <form onSubmit={addCounty} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('settings.countyName')}</Label>
              <Input data-testid="county-name-input" value={countyName} onChange={e => setCountyName(e.target.value)} placeholder={t('settings.countyPlaceholder')} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCountyDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">{t('settings.addCounty')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={helpDialogOpen} onOpenChange={setHelpDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingArticle ? t('settings.editArticle') : t('settings.newArticle')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveArticle} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('settings.articleTitle')} *</Label>
              <Input data-testid="article-title" value={articleForm.title} onChange={e => setArticleForm({ ...articleForm, title: e.target.value })} placeholder={t('settings.articleTitlePlaceholder')} />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.articleCategory')}</Label>
              <Input value={articleForm.category} onChange={e => setArticleForm({ ...articleForm, category: e.target.value })} placeholder="general, estimados, trabajos..." />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.articleContent')}</Label>
              <Textarea value={articleForm.content} onChange={e => setArticleForm({ ...articleForm, content: e.target.value })} placeholder={t('settings.articleContentPlaceholder')} rows={6} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setHelpDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button data-testid="save-article-btn" type="submit" className="bg-[#003366] hover:bg-[#004080] text-white">
                {editingArticle ? t('common.save') : t('common.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Creation / Edit Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={(open) => { if (!open) { closeUserDialog(); } }}>
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editingUserId ? <Pencil className="w-5 h-5 text-[#003366]" /> : <UserPlus className="w-5 h-5 text-[#003366]" />}
              {editingUserId
                ? t('settings.editUser', { defaultValue: 'Edit User' })
                : t('settings.addUser')}
            </DialogTitle>
          </DialogHeader>
          <form id="user-edit-form" onSubmit={saveUser} className="space-y-4 overflow-y-auto flex-1 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('settings.firstName')} *</Label>
                <Input 
                  data-testid="user-first-name" 
                  value={userForm.first_name} 
                  onChange={e => setUserForm({ ...userForm, first_name: e.target.value })} 
                  placeholder="Carlos"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('settings.lastName')}</Label>
                <Input 
                  data-testid="user-last-name" 
                  value={userForm.last_name} 
                  onChange={e => setUserForm({ ...userForm, last_name: e.target.value })} 
                  placeholder="Rodriguez"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('settings.email')} *</Label>
              <Input 
                data-testid="user-email" 
                type="email"
                value={userForm.email} 
                onChange={e => setUserForm({ ...userForm, email: e.target.value })} 
                placeholder="carlos@bluewell.pro"
              />
            </div>
            <div className="space-y-2">
              <Label>
                {editingUserId
                  ? t('settings.newPasswordOptional', { defaultValue: 'New password (leave empty to keep current)' })
                  : `${t('settings.password')} *`}
              </Label>
              <Input 
                data-testid="user-password" 
                type="password"
                value={userForm.password} 
                onChange={e => setUserForm({ ...userForm, password: e.target.value })} 
                placeholder={editingUserId ? '' : '••••••••'}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.phone')}</Label>
              <Input 
                data-testid="user-phone" 
                value={userForm.phone} 
                onChange={e => setUserForm({ ...userForm, phone: formatPhoneNumber(e.target.value) })} 
                placeholder="(786) 510-4512"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('settings.usersTable.role')}</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          role.value === 'admin' ? 'bg-blue-500' :
                          role.value === 'office' ? 'bg-green-500' :
                          role.value === 'driller' ? 'bg-amber-500' :
                          role.value === 'pump_installer' ? 'bg-cyan-500' :
                          'bg-gray-500'
                        }`} />
                        {t(role.labelKey, { defaultValue: role.value })}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-400">
                {userForm.role === 'driller' && t('settings.roleDescDriller')}
                {userForm.role === 'pump_installer' && t('settings.roleDescPumpInstaller')}
                {userForm.role === 'office' && t('settings.roleDescOffice')}
                {userForm.role === 'admin' && t('settings.roleDescAdmin')}
                {userForm.role === 'contractor' && t('settings.roleDescContractor', { defaultValue: 'Limited portal: sees only their own estimates, invoices and jobs, and can pay invoices. No internal/financial data.' })}
              </p>
            </div>

            {userForm.role === 'contractor' && (
              <div className="space-y-2 rounded-lg border border-blue-100 bg-blue-50/40 p-3" data-testid="user-contractor-link">
                <Label className="text-sm font-semibold text-gray-700">Linked Contact / Company</Label>
                <p className="text-xs text-gray-500 -mt-1">The contractor will only see estimates, invoices and jobs tied to this contact.</p>
                <ContactCombobox
                  contacts={contacts}
                  value={userForm.contact_id}
                  onChange={(id) => setUserForm({ ...userForm, contact_id: id })}
                  testId="user-contact-combobox"
                  placeholder="Select the contractor's contact/company..."
                />
              </div>
            )}

            {/* Compensation / Pay configuration (admin-only, sensitive) */}
            <div className="rounded-lg border border-amber-100 bg-amber-50/40 p-3 space-y-3" data-testid="user-pay-config">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-amber-600" />
                <Label className="text-sm font-semibold text-gray-700">Compensation (admin only)</Label>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Payment type</Label>
                  <Select value={userForm.pay_type} onValueChange={v => setUserForm({ ...userForm, pay_type: v })}>
                    <SelectTrigger data-testid="user-pay-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not set</SelectItem>
                      <SelectItem value="commission">Commission (%)</SelectItem>
                      <SelectItem value="fixed">Fixed amount (period)</SelectItem>
                      <SelectItem value="per_well">Per well</SelectItem>
                      <SelectItem value="per_job">Per job</SelectItem>
                      <SelectItem value="per_day">Per day</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">
                    {userForm.pay_type === 'commission' ? 'Commission (%) — enter 5 for 5%' : 'Amount ($)'}
                  </Label>
                  <Input
                    data-testid="user-pay-value-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={userForm.pay_value}
                    onChange={e => setUserForm({ ...userForm, pay_value: e.target.value })}
                    disabled={userForm.pay_type === 'none'}
                    placeholder={userForm.pay_type === 'commission' ? '10' : '0.00'}
                  />
                  {userForm.pay_type === 'commission' && (
                    <>
                      {/* Alian escribió 0.5/0.4 creyendo que era 5%/4% — vista previa
                          en vivo con un monto real para que vea el resultado mientras
                          escribe, sin tener que auditarlo después con una calculadora. */}
                      <p className="text-[11px] text-amber-700" data-testid="user-pay-value-hint">
                        Enter the full percentage number (e.g. <strong>5</strong> for 5%, not 0.05).{' '}
                        <strong>{parseFloat(userForm.pay_value) || 0}%</strong> of a <strong>$6,120</strong> job = <strong>${((parseFloat(userForm.pay_value) || 0) / 100 * 6120).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
                      </p>
                      {parseFloat(userForm.pay_value) > 0 && parseFloat(userForm.pay_value) < 1 && (
                        <p className="text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded px-2 py-1 mt-1" data-testid="user-pay-value-suspicious-warning">
                          Did you mean {(parseFloat(userForm.pay_value) * 10).toLocaleString(undefined, { maximumFractionDigits: 1 })}%? You're entering {parseFloat(userForm.pay_value).toLocaleString(undefined, { maximumFractionDigits: 2 })}%.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-amber-700/80">
                Used to calculate the commission / payout report per person.
              </p>
            </div>

            {/* Default job category — pre-suggests the job type when this person is assigned */}
            <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-2" data-testid="user-default-category-config">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-indigo-600" />
                <Label className="text-sm font-semibold text-gray-700">Default category</Label>
              </div>
              <Select
                value={userForm.default_job_category || 'none'}
                onValueChange={v => setUserForm({ ...userForm, default_job_category: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="user-default-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No default category</SelectItem>
                  {JOB_CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-indigo-700/80">
                When you assign this person to a job, the system will suggest this category (always editable).
              </p>
            </div>
            {notificationEvents.length > 0 && (
              <div className="rounded-lg border border-sky-100 bg-sky-50/40 p-3 space-y-2" data-testid="user-notification-preferences">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-sky-600" />
                  <Label className="text-sm font-semibold text-gray-700">Notifications</Label>
                </div>
                <p className="text-[11px] text-sky-700/80 -mt-1">
                  Which in-app alerts should this person receive? Admin/office/manager get all of these by default; other roles get none until checked here.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {notificationEvents.map(ev => {
                    const explicit = userForm.notification_preferences?.[ev.key];
                    const checked = explicit === true || (explicit === undefined && ['admin', 'superadmin', 'office', 'manager'].includes(userForm.role));
                    return (
                      <label key={ev.key} className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                        <Checkbox
                          data-testid={`user-notif-${ev.key}`}
                          checked={checked}
                          onCheckedChange={(v) => setUserForm(prev => ({
                            ...prev,
                            notification_preferences: { ...(prev.notification_preferences || {}), [ev.key]: !!v },
                          }))}
                        />
                        {ev.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {editingUserId && (
              <div className="flex items-center justify-between rounded-md border border-gray-100 px-3 py-2">
                <div>
                  <Label className="text-sm font-medium">
                    {t('settings.userActive', { defaultValue: 'Active' })}
                  </Label>
                  <p className="text-xs text-gray-400">
                    {t('settings.userActiveHint', { defaultValue: 'Inactive users cannot log in' })}
                  </p>
                </div>
                <Switch
                  data-testid="user-active-toggle"
                  checked={userForm.is_active}
                  onCheckedChange={(checked) => setUserForm({ ...userForm, is_active: checked })}
                />
              </div>
            )}
          </form>
          <DialogFooter className="pt-3 border-t border-gray-100 flex-shrink-0">
            <Button type="button" variant="outline" onClick={closeUserDialog}>
              {t('common.cancel')}
            </Button>
            <Button data-testid="save-user-btn" type="submit" form="user-edit-form" className="bg-[#003366] hover:bg-[#004080] text-white">
              {editingUserId ? (
                <><Save className="w-4 h-4 mr-1" /> {t('common.save', { defaultValue: 'Save' })}</>
              ) : (
                <><UserPlus className="w-4 h-4 mr-1" /> {t('settings.createUser')}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
